<template>
    <div>
        <el-row
            type="flex"
            justify="space-between"
            align="middle"
        >
            <el-col>
                <h2>{{ $t("view.users.users") }}</h2>
            </el-col>
            <el-col class="demands-header-action-buttons">
                &nbsp;
            </el-col>
        </el-row>

        <el-table
            ref="table"
            v-loading="loading"
            :data="getUsers"
            :row-class-name="tableRowClassName"
            stripe
            fit
        >
            <el-table-column type="expand">
                <template slot-scope="props">
                    <p>
                        <strong>{{ $t("view.users.email") }}</strong>:
                        <a
                            :href="`mailto:${props.row.email}`"
                        >
                            {{ props.row.email }}
                        </a>
                    </p>
                    <p>
                        <strong>{{ $t("view.users.email") }}</strong>:
                        <a
                            v-if="props.row.phoneNumber !== ''"
                            :href="`tel:${props.row.phoneNumber}`"
                        >
                            {{ props.row.phoneNumber }}
                        </a>
                        <span
                            v-if="props.row.phoneNumber === ''"
                        >-</span>
                    </p>
                    <p><strong>{{ $t("view.users.created_at") }}</strong>:
                        {{ humanReadableDate(props.row.createTimestamp) }}</p>
                    <p><strong>{{ $t("view.users.updated_at") }}</strong>:
                        {{ humanReadableDate(props.row.updateTimestamp) }}</p>
                </template>
            </el-table-column>
            <el-table-column
                width="60px"
            >
                <template slot-scope="scope">
                    <img
                        :src="scope.row.photoURL"
                        width="48px;"
                    />
                </template>
            </el-table-column>
            <el-table-column
                :label="$t('view.users.display_name')"
            >
                <template slot-scope="scope">
                    <el-dropdown
                        placement="bottom-start"
                        @command="handleUserCommand($event, scope.row)"
                    >
                        <span class="el-dropdown-link">
                            {{ scope.row.displayName }}
                        </span>
                        <el-dropdown-menu
                            slot="dropdown"
                        >
                            <el-dropdown-item
                                icon="el-icon-edit"
                                command="edit"
                            >
                                {{ $t("view.users.edit") }}
                            </el-dropdown-item>
                            <el-dropdown-item
                                v-if="scope.row.role !== ''"
                                icon="el-icon-circle-close"
                                command="deactivate"
                            >
                                {{ $t("view.users.deactivate") }}
                            </el-dropdown-item>
                            <el-dropdown-item
                                v-if="scope.row.role === ''"
                                icon="el-icon-circle-check"
                                command="activate"
                            >
                                {{ $t("view.users.activate") }}
                            </el-dropdown-item>
                        </el-dropdown-menu>
                    </el-dropdown>
                </template>
            </el-table-column>
            <el-table-column
                :label="$t('view.users.notification_type')"
                align="center"
                width="140px"
            >
                <template slot-scope="scope">
                    <el-tooltip
                        class="item"
                        effect="dark"
                        :content="true === scope.row.notificationMethod ? $t('view.users.sms', {number: scope.row.phoneNumber}) : $t('view.users.push_notification')"
                        placement="top"
                    >
                        <i
                            :class="{'el-icon-message-solid': !scope.row.notificationMethod, 'el-icon-chat-dot-round': true === scope.row.notificationMethod}"
                        />
                    </el-tooltip>
                </template>
            </el-table-column>
            <el-table-column
                prop="role"
                :label="$t('view.users.role')"
                width="150px"
            >
                <template slot-scope="scope">
                    {{ mapRole(scope.row.role) }}
                </template>
            </el-table-column>
        </el-table>
    </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import moment                   from "moment";

export default {
    name: "Users",
    head() {
        return {
            title: {
                inner: this.$t("view.users.users")
            }
        };
    },
    data    : () => (
        {
            loading: false
        }
    ),
    computed: {
        ...mapGetters("authentication", ["getUsers"])
    },
    async mounted() {
        if (this.getUsers.length === 0) {
            this.loading = true;
            await this.getUsersAction();
            this.loading = false;
        }
    },
    methods: {
        ...mapActions(
            "authentication",
            [
                "getUsersAction",
                "updateUser"
            ]
        ),
        mapRole(role) {
            switch (role) {
                case "administrator":
                    return this.$t("view.users.administrator");
                case "agent":
                    return this.$t("view.users.agent");
                default:
                    return this.$t("view.users.unknown");
            }
        },
        humanReadableDate(date) {
            moment.locale("el");
            return moment(date).fromNow();
        },
        tableRowClassName({row}) {
            if (row.role === "") {
                return "deactivated-user";
            }

            return "";
        },
        handleUserCommand(command, row) {
            switch (command) {
                case "edit":
                    this
                        .$router
                        .push(
                            {
                                name  : "profile",
                                params: {
                                    id: row.id
                                },
                                query : {
                                    mode: "edit"
                                }
                            }
                        );
                    break;
                case "activate":
                    this.unblockUserAccess(row.id);
                    break;
                case "deactivate":
                    this.blockUserAccess(row.id);
                    break;
                default:
                    return null;
            }

            return null;
        },
        async blockUserAccess(userID) {
            const filteredUser = this.getUsers.find(user => user.id === userID);
            this.loading       = true;
            const userToUpdate = JSON.parse(JSON.stringify(filteredUser));
            userToUpdate.role  = "";
            await this.updateUser(userToUpdate);
            await this.getUsersAction();
            this.loading = false;

            this
                .$notify(
                    {
                        title                   : this.$t("view.users.deactivation_notification_title"),
                        message                 : this.$t(
                            "view.users.deactivation_notification_message",
                            {
                                username: filteredUser.displayName
                            }
                        ),
                        type                    : "success",
                        dangerouslyUseHTMLString: true
                    }
                );
        },
        async unblockUserAccess(userID) {
            const filteredUser = this.getUsers.find(user => user.id === userID);
            this.loading       = true;
            const userToUpdate = JSON.parse(JSON.stringify(filteredUser));
            userToUpdate.role  = "agent";
            await this.updateUser(userToUpdate);
            await this.getUsersAction();
            this.loading = false;

            this
                .$notify(
                    {
                        title                   : this.$t("view.users.activation_notification_title"),
                        message                 : this.$t(
                            "view.users.activation_notification_message",
                            {
                                username: filteredUser.displayName
                            }
                        ),
                        type                    : "success",
                        dangerouslyUseHTMLString: true
                    }
                );
        }
    }
};
</script>

<style lang="scss">
.el-table .deactivated-user {
    background : #FFDADA;
    color      : #999;

    &:hover {
        background : #FFDADA !important;
        color      : #999 !important;

        & > td {
            background : #FFDADA !important;
            color      : #999 !important;
        }
    }
}

.el-dropdown-link {
    cursor    : pointer;
    color     : #409EFF;
    font-size : 14px;
}

.el-icon-arrow-down {
    font-size : 12px;
}
</style>